import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import { Dialog } from '../ui/Dialog/Dialog';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { skipQuarantine } from '../../services/quarantineService';
import { StartQuarantineForm, type QuarantineAnimal } from './StartQuarantineForm';

/**
 * After adding a fish, coral or invertebrate (#1826): "Quarantined?" — start a
 * protocol, already quarantined, or skip. Both answers without a protocol are
 * recorded once and never asked again for this animal.
 */
export function QuarantinePromptDialog({ aquariumId, animal, onClose }: {
  aquariumId: string;
  animal: QuarantineAnimal | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [choosing, setChoosing] = useState(false);
  const [error, setError] = useState(false);
  const close = () => {
    setChoosing(false);
    setError(false);
    onClose();
  };
  const skip = async () => {
    if (!animal) return;
    try {
      await skipQuarantine(aquariumId, animal.id);
      close();
    } catch {
      setError(true);
    }
  };
  return (
    <Dialog isOpen={animal !== null} onClose={close} title={t('quarantine.prompt.title', { name: animal?.name ?? '' })} size="sm" data-testid="quarantine-prompt">
      {animal && (choosing ? (
        <StartQuarantineForm
          aquariumId={aquariumId}
          animal={animal}
          onStarted={() => {
            close();
            navigate(`/aquariums/${aquariumId}/quarantine`);
          }}
          onCancel={() => setChoosing(false)}
          cancelLabel={t('quarantine.back')}
        />
      ) : (
        <Stack gap="4">
          <Text>{t('quarantine.prompt.body')}</Text>
          {error && <Alert>{t('quarantine.prompt.error')}</Alert>}
          <Inline gap="2" wrap>
            <Button onClick={() => setChoosing(true)}>{t('quarantine.prompt.start')}</Button>
            <Button variant="secondary" onClick={() => void skip()}>{t('quarantine.prompt.already')}</Button>
            <Button variant="ghost" onClick={() => void skip()}>{t('quarantine.prompt.skip')}</Button>
          </Inline>
        </Stack>
      ))}
    </Dialog>
  );
}
